import { useEffect, useRef, useState } from "react";
import { X, Zap, Upload, Image as ImageIcon, Camera, Loader2 } from "lucide-react";

// Skener dokladů na telefonu (198, návrh B): živý náhled zadní kamery s rámečkem,
// přepínač Faktura / Účtenka, spoušť, PDF/soubor a galerie. Počítač ho nevidí —
// useDocPicker ho otevře jen na telefonu (úzký displej + dotyk).
// Odolnost (Samsung, Xiaomi, iPhone):
//   - kamera přes getUserMedia jen s „ideal“ omezeními (nikdy nespadne na rozlišení),
//   - snímek = aktuální obraz videa v plném rozlišení (bez otáčení EXIF, bez čekání),
//   - když kamera nejde (zakázaná, http, starý prohlížeč, slabé rozlišení), spoušť
//     otevře foťák telefonu (input capture) — doklad jde nafotit vždycky,
//   - při odchodu do jiné aplikace se kamera vypne a po návratu znovu zapne,
//   - systémové Zpět na Androidu skener zavře.

const MIN_SHORT_SIDE = 720;   // pod tím (staré telefony) raději foťák telefonu

function stopStream(s) { try { s?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } }

export default function DocScanner({ type, choose = false, onType, onClose, onShot, onCamera, onGallery, onFile }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [state, setState] = useState("starting");     // starting | live | fallback
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [torch, setTorch] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [run, setRun] = useState(0);                   // přepnutím se kamera spustí znovu

  // kamera: zapnout při otevření a po návratu do aplikace, vypnout při odchodu
  useEffect(() => {
    let alive = true;
    const md = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
    if (!md || typeof md.getUserMedia !== "function") {
      setState("fallback"); setReason("Tady se kamera v prohlížeči nespustí.");
      return undefined;
    }
    setState("starting");
    md.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then(async (stream) => {
        if (!alive) { stopStream(stream); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          await v.play().catch(() => {});
        }
        const track = stream.getVideoTracks()[0];
        try {
          const caps = track?.getCapabilities?.() || {};
          setHasTorch(!!caps.torch);
          if ((caps.focusMode || []).includes("continuous")) await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
        } catch { /* ostření a svítilnu neumí každý telefon */ }
        if (alive) setState("live");
      })
      .catch((e) => {
        if (!alive) return;
        setState("fallback");
        setReason(e?.name === "NotAllowedError" || e?.name === "SecurityError"
          ? "Kamera je pro aplikaci zakázaná — povol ji v nastavení prohlížeče."
          : "Kameru se nepodařilo spustit.");
      });
    const vis = () => {
      if (document.visibilityState === "hidden") { stopStream(streamRef.current); streamRef.current = null; }
      else if (!streamRef.current) setRun((n) => n + 1);
    };
    document.addEventListener("visibilitychange", vis);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", vis);
      stopStream(streamRef.current); streamRef.current = null;
    };
  }, [run]);

  // Zpět (Android) zavře skener; zamknout posun stránky pod ním
  useEffect(() => {
    let popped = false;
    const onPop = () => { popped = true; onClose?.(); };
    try { window.history.pushState({ ...(window.history.state || {}), ppScan: true }, ""); } catch { /* noop */ }
    window.addEventListener("popstate", onPop);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const esc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = prev;
      if (!popped && window.history.state && window.history.state.ppScan) { try { window.history.back(); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flipTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch((t) => !t); } catch { /* noop */ }
  };

  const shoot = async () => {
    if (busy) return;
    const v = videoRef.current;
    const w = v?.videoWidth || 0, h = v?.videoHeight || 0;
    // bez živého obrazu nebo se slabou kamerou: foťák telefonu (vyšší kvalita)
    if (state !== "live" || !w || !h || Math.min(w, h) < MIN_SHORT_SIDE) { nativeCamera(); return; }
    setBusy(true);
    try {
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(v, 0, 0, w, h);
      const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.92));
      if (!blob) throw new Error("toBlob");
      try { navigator.vibrate?.(30); } catch { /* noop */ }
      let file;
      try { file = new File([blob], `doklad-${Date.now()}.jpg`, { type: "image/jpeg" }); }
      catch { file = Object.assign(blob, { name: `doklad-${Date.now()}.jpg` }); }
      stopStream(streamRef.current); streamRef.current = null;
      onShot?.(file);
    } catch {
      setBusy(false);
      nativeCamera();
    }
  };
  // foťák telefonu: nejdřív pustit kameru, jinak ji Android aplikaci foťáku nedá
  // (po návratu do prohlížeče se náhled sám zapne — visibilitychange)
  const nativeCamera = () => { stopStream(streamRef.current); streamRef.current = null; onCamera?.(); };

  const isF = type !== "uctenka";
  const kind = isF ? "fakturu" : "účtenku";
  const kindBtn = (t, label) => (
    <button type="button" role="radio" aria-checked={type === t} onClick={() => onType?.(t)} data-testid={`scan-type-${t}`}
      className={`h-9 px-[18px] rounded-full text-sm font-bold transition-colors duration-200 motion-reduce:transition-none ${type === t ? "bg-white text-[#0f0f10]" : "text-white"}`}>{label}</button>
  );
  const side = (Icon, label, onClick, testid) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className="w-16 flex flex-col items-center gap-1.5 text-xs font-semibold text-white/85 active:scale-95 transition-transform motion-reduce:transition-none">
      <span className="w-12 h-12 rounded-full bg-white/[.14] grid place-items-center"><Icon size={20} aria-hidden="true" /></span>{label}
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label={`Naskenovat ${kind}`} data-testid="doc-scanner"
      className="fixed inset-0 z-[70] bg-[#0b0b0c] text-white select-none pp-scan-in" style={{ touchAction: "none" }}>
      <video ref={videoRef} playsInline muted autoPlay aria-hidden="true"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${state === "live" ? "opacity-100" : "opacity-0"}`} />

      {/* rámeček: okolí ztmavené, rohy bílé */}
      <div aria-hidden="true" className="absolute left-8 right-8 rounded-[18px] pointer-events-none"
        style={{ top: "calc(max(14px, env(safe-area-inset-top)) + 72px)", bottom: "calc(env(safe-area-inset-bottom) + 236px)", boxShadow: "0 0 0 9999px rgba(0,0,0,.45)" }}>
        <span className="absolute left-0 top-0 w-9 h-9 border-l-4 border-t-4 border-white rounded-tl-[18px]" />
        <span className="absolute right-0 top-0 w-9 h-9 border-r-4 border-t-4 border-white rounded-tr-[18px]" />
        <span className="absolute left-0 bottom-0 w-9 h-9 border-l-4 border-b-4 border-white rounded-bl-[18px]" />
        <span className="absolute right-0 bottom-0 w-9 h-9 border-r-4 border-b-4 border-white rounded-br-[18px]" />
        {state !== "live" && (
          <span className="absolute inset-0 grid place-items-center px-6 text-center">
            {state === "starting"
              ? <Loader2 size={28} className="animate-spin motion-reduce:animate-none text-white/70" />
              : <span className="block" data-testid="scan-fallback">
                  <Camera size={34} className="mx-auto text-white/80" />
                  <span className="block mt-3 text-[15px] font-semibold">{reason}</span>
                  <span className="block mt-1 text-sm text-white/70">Klepni na spoušť a vyfoť doklad foťákem telefonu.</span>
                </span>}
          </span>
        )}
      </div>

      {/* horní řádek */}
      <div className="absolute left-3.5 right-3.5 flex items-center justify-between" style={{ top: "max(14px, calc(env(safe-area-inset-top) + 6px))" }}>
        <button type="button" onClick={onClose} aria-label="Zavřít skener" data-testid="scan-close"
          className="w-11 h-11 rounded-full bg-white/[.14] grid place-items-center active:scale-95 transition-transform motion-reduce:transition-none"><X size={20} aria-hidden="true" /></button>
        <span className="text-base font-bold">Naskenovat {kind}</span>
        {hasTorch && state === "live"
          ? <button type="button" onClick={flipTorch} aria-label="Svítilna" aria-pressed={torch} data-testid="scan-torch"
              className={`w-11 h-11 rounded-full grid place-items-center ${torch ? "bg-brand" : "bg-white/[.14]"}`}><Zap size={18} aria-hidden="true" /></button>
          : <span className="w-11" />}
      </div>

      {/* spodní ovládání */}
      <div className="absolute inset-x-0" style={{ bottom: "calc(env(safe-area-inset-bottom) + 24px)" }}>
        <p className="text-center text-sm text-white/75 mb-5">{state === "live" ? "Vejdi celým dokladem do rámečku" : " "}</p>
        {choose && (
          <div className="flex justify-center mb-5">
            <div role="radiogroup" aria-label="Druh dokladu" className="flex bg-white/[.12] rounded-full p-1">
              {kindBtn("faktura", "Faktura")}{kindBtn("uctenka", "Účtenka")}
            </div>
          </div>
        )}
        <div className="flex items-center justify-around px-7">
          {side(Upload, isF ? "PDF" : "Soubor", onFile, "scan-file")}
          <button type="button" onClick={shoot} disabled={busy} aria-label={`Vyfotit ${kind}`} data-testid="scan-shutter"
            className="w-[76px] h-[76px] rounded-full grid place-items-center shadow-[inset_0_0_0_4px_#fff] active:scale-95 transition-transform motion-reduce:transition-none disabled:opacity-70">
            <span className="w-[60px] h-[60px] rounded-full bg-white grid place-items-center">
              {busy && <Loader2 size={24} className="animate-spin motion-reduce:animate-none text-[#0f0f10]" />}
            </span>
          </button>
          {side(ImageIcon, "Galerie", onGallery, "scan-gallery")}
        </div>
      </div>
    </div>
  );
}
